import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createOrder,
  decideOrder,
  deleteOrder,
  downloadOrderPdf,
  listOrders,
  signOrder,
  submitOrder,
} from 'service/OrdersService'
import { listStudents } from 'service/StudentsService'
import Notification from 'utils/Notifications'

import OrdersToolbar from './components/OrdersToolbar/OrdersToolbar'
import OrdersTable from './components/OrdersTable/OrdersTable'
import OrderCreateForm from './components/OrderCreateForm/OrderCreateForm'
import OrderDetailPanel from './components/OrderDetailPanel/OrderDetailPanel'
import {
  PageHeader,
  UnvButton,
  Skeleton,
  EmptyState,
  ErrorState,
} from 'components'
import styles from './OrdersPage.module.scss'

export default function OrdersPage() {
  const { t } = useTranslation('common')
  const [orders, setOrders] = useState([])
  const [students, setStudents] = useState([])
  const [filter, setFilter] = useState({ status: '', kind: '', q: '' })
  const [drawer, setDrawer] = useState(null)
  const [busy, setBusy] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const reload = async () => {
    setBusy(true)
    setLoadError(false)
    try {
      const params = {}
      if (filter.status) params.status = filter.status
      if (filter.kind) params.kind = filter.kind
      if (filter.q) params.q = filter.q
      const r = await listOrders(params)
      setOrders(r.data?.results || r.data || [])
    } catch (e) {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('ordersPage.cantLoad'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => { reload() }, [filter.status, filter.kind])
  useEffect(() => {
    listStudents({ page_size: 200 })
      .then((r) => setStudents(r.data?.results || r.data || []))
      .catch(() => {})
  }, [])

  const handleCreate = async () => {
    const v = drawer
    if (!v?.student || !v?.kind) {
      setNotify({ isOpen: true, message: t('ordersPage.selectStudentAndKind'), type: 'warning' })
      return
    }
    setBusy(true)
    try {
      const r = await createOrder({
        student: v.student,
        kind: v.kind,
        title: v.title,
        body: v.body,
        reason: v.reason,
        effective_at: v.effective_at,
        extra_data: v.extra_data || {},
      })
      setNotify({ isOpen: true, message: t('ordersPage.orderCreated'), type: 'success' })
      setDrawer(r.data)
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || t('ordersPage.error'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleSubmit = async () => {
    if (!drawer?.id) return
    setBusy(true)
    try {
      const r = await submitOrder(drawer.id)
      setDrawer(r.data)
      setNotify({ isOpen: true, message: t('ordersPage.submittedReview'), type: 'success' })
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || t('ordersPage.error'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleDecide = async (role_code, decision) => {
    if (!drawer?.id) return
    const note = decision === 'rejected' ? prompt(t('ordersPage.rejectReason')) : ''
    setBusy(true)
    try {
      await decideOrder(drawer.id, { role_code, decision, note })
      const r = await listOrders({})
      const fresh = (r.data?.results || r.data || []).find((o) => o.id === drawer.id)
      setDrawer(fresh || drawer)
      setOrders(r.data?.results || r.data || [])
      setNotify({ isOpen: true, message: t('ordersPage.saved'), type: 'success' })
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || t('ordersPage.error'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleSign = async () => {
    if (!drawer?.id) return
    if (!window.confirm(t('ordersPage.confirmSign'))) return
    setBusy(true)
    try {
      const r = await signOrder(drawer.id)
      setDrawer(r.data)
      setNotify({ isOpen: true, message: t('ordersPage.signed'), type: 'success' })
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || t('ordersPage.error'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handlePdf = async () => {
    if (!drawer?.id) return
    try {
      const r = await downloadOrderPdf(drawer.id)
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `order_${(drawer.number || drawer.id).toString().replace('/', '_')}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      setNotify({ isOpen: true, message: t('ordersPage.cantPdf'), type: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!drawer?.id) return
    if (!window.confirm(t('ordersPage.confirmDelete'))) return
    try {
      await deleteOrder(drawer.id)
      setDrawer(null)
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: t('ordersPage.cantDelete'), type: 'error' })
    }
  }

  const openNew = () =>
    setDrawer({
      id: null,
      status: 'draft',
      kind: 'academic_leave',
      student: null,
      title: '',
      body: '',
      reason: '',
      extra_data: {},
    })

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('ordersPage.title', 'Приказы')}
        subtitle={t('ordersPage.subtitle', 'Управление приказами по студентам')}
        actions={
          <UnvButton variant="primary" iconLeft="plus" onClick={openNew}>
            {t('ordersPage.newOrderTitle', 'Новый приказ')}
          </UnvButton>
        }
      />

      <OrdersToolbar
        filter={filter}
        setFilter={setFilter}
        onReload={reload}
        onNew={openNew}
      />

      {busy && orders.length === 0 ? (
        <Skeleton variant="table" rows={6} cols={5} />
      ) : loadError ? (
        <ErrorState title={t('ordersPage.cantLoad')} onRetry={reload} />
      ) : orders.length === 0 ? (
        <EmptyState iconName="folder" title={t('ordersPage.no_data', 'Приказов не найдено')} description={t('ordersPage.no_data_desc', 'Создайте новый приказ или измените фильтры')} />
      ) : (
        <OrdersTable orders={orders} onSelect={setDrawer} />
      )}

      {drawer && (
        <div className={styles.overlay} onClick={() => setDrawer(null)}>
          <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.head}>
              <h3>
                {drawer.id ? `${t('ordersPage.orderNo')} ${drawer.number}` : t('ordersPage.newOrderTitle')}
              </h3>
              <button className={styles.close} onClick={() => setDrawer(null)}>×</button>
            </div>

            {!drawer.id && (
              <OrderCreateForm
                drawer={drawer}
                setDrawer={setDrawer}
                students={students}
                busy={busy}
                onCreate={handleCreate}
                onCancel={() => setDrawer(null)}
              />
            )}

            {drawer.id && (
              <OrderDetailPanel
                drawer={drawer}
                busy={busy}
                onSubmit={handleSubmit}
                onSign={handleSign}
                onPdf={handlePdf}
                onDelete={handleDelete}
                onDecide={handleDecide}
              />
            )}
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}
